import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Download, 
  FileText, 
  FileSpreadsheet, 
  Users, 
  Calendar,
  Clock,
  DollarSign,
  TrendingUp,
  Filter,
  ChevronDown,
  Loader2,
  FileDown,
  Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent, 
  DropdownMenuItem 
} from '@/components/ui/dropdown-menu';

export default function Reports() {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [selectedReport, setSelectedReport] = useState('employees');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  
  // Report data states
  const [claimsData, setClaimsData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [leaveData, setLeaveData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);

  const reportTypes = [
    { id: 'employees', name: 'Employees', icon: Users, color: 'blue' },
    { id: 'payroll', name: 'Payroll', icon: DollarSign, color: 'emerald' },
    { id: 'claims', name: 'Claims', icon: FileText, color: 'purple' },
    { id: 'attendance', name: 'Attendance', icon: Clock, color: 'orange' },
    { id: 'leave', name: 'Leave', icon: Calendar, color: 'pink' },
    { id: 'performance', name: 'Performance', icon: TrendingUp, color: 'teal' },
  ];

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [2023, 2024, 2025, 2026];

  const fetchReportData = async (reportType) => {
    setLoading(true);
    try {
      let url = '';
      switch (reportType) {
        case 'claims':
          url = `${API}/reports/claims?month=${month}&year=${year}`;
          const claimsRes = await axios.get(url);
          setClaimsData(claimsRes.data);
          break;
        case 'attendance':
          url = `${API}/reports/attendance?month=${month}&year=${year}`;
          const attendanceRes = await axios.get(url);
          setAttendanceData(attendanceRes.data);
          break;
        case 'leave':
          url = `${API}/reports/leave?year=${year}`;
          const leaveRes = await axios.get(url);
          setLeaveData(leaveRes.data);
          break;
        case 'performance':
          url = `${API}/reports/performance?year=${year}`;
          const perfRes = await axios.get(url);
          setPerformanceData(perfRes.data);
          break;
        default:
          break;
      }
      toast.success('Report data loaded');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportType, format = 'excel') => {
    setDownloadLoading(`${reportType}-${format}`);
    try {
      let url = '';
      let filename = '';
      const monthName = months.find(m => m.value === month)?.label || 'Unknown';
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';

      switch (reportType) {
        case 'employees':
          url = `${API}/reports/employees/download?format=${format}`;
          filename = `Employee_Report_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'payroll':
          url = `${API}/reports/payroll/download?month=${month}&year=${year}&format=${format}`;
          filename = `Payroll_Report_${monthName}_${year}`;
          break;
        case 'claims':
          url = `${API}/reports/claims/download?month=${month}&year=${year}&format=${format}`;
          filename = `Claims_Report_${monthName}_${year}`;
          break;
        case 'attendance':
          url = `${API}/reports/attendance/download?month=${month}&year=${year}&format=${format}`;
          filename = `Attendance_Report_${monthName}_${year}`;
          break;
        case 'leave':
          url = `${API}/reports/leave/download?year=${year}&format=${format}`;
          filename = `Leave_Report_${year}`;
          break;
        case 'performance':
          url = `${API}/reports/performance/download?year=${year}&format=${format}`;
          filename = `Performance_Report_${year}`;
          break;
        default:
          return;
      }

      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${filename}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadLoading(null);
    }
  };

  const seedDemoData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/seed-demo-data`);
      toast.success(`Demo data created: ${response.data.results.employees} employees, ${response.data.results.claims} claims, ${response.data.results.attendance} attendance records`);
    } catch (error) {
      console.error('Seed error:', error);
      toast.error('Failed to seed demo data');
    } finally {
      setLoading(false);
    }
  };

  const getColorClass = (color) => {
    const colors = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
      teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Reports & Analytics
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Generate and download comprehensive HR reports
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filters:</span>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600 dark:text-slate-400">Month</Label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="h-9 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              data-testid="month-filter"
            >
              {months.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600 dark:text-slate-400">Year</Label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="h-9 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              data-testid="year-filter"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const isLoading = downloadLoading === report.id;
          
          return (
            <div 
              key={report.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`report-card-${report.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`h-12 w-12 rounded-xl ${getColorClass(report.color)} flex items-center justify-center`}>
                  <Icon className="h-6 w-6" />
                </div>
                {['claims', 'attendance', 'leave', 'performance'].includes(report.id) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchReportData(report.id)}
                    disabled={loading}
                    className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    data-testid={`preview-${report.id}-btn`}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview'}
                  </Button>
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                {report.name} Report
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                {report.id === 'employees' && 'Complete employee directory with details'}
                {report.id === 'payroll' && `Payroll summary for ${months.find(m => m.value === month)?.label} ${year}`}
                {report.id === 'claims' && `Claims analytics for ${months.find(m => m.value === month)?.label} ${year}`}
                {report.id === 'attendance' && `Attendance records for ${months.find(m => m.value === month)?.label} ${year}`}
                {report.id === 'leave' && `Leave applications for ${year}`}
                {report.id === 'performance' && `Performance reviews for ${year}`}
              </p>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => downloadReport(report.id, 'excel')}
                  disabled={downloadLoading === `${report.id}-excel`}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                  data-testid={`download-${report.id}-excel-btn`}
                >
                  {downloadLoading === `${report.id}-excel` ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  )}
                  Excel
                </Button>
                <Button
                  onClick={() => downloadReport(report.id, 'pdf')}
                  disabled={downloadLoading === `${report.id}-pdf`}
                  variant="outline"
                  className="flex-1 border-slate-300 dark:border-slate-600"
                  data-testid={`download-${report.id}-pdf-btn`}
                >
                  {downloadLoading === `${report.id}-pdf` ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="h-4 w-4 mr-2" />
                  )}
                  PDF
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Seed Demo Data Button */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">Demo Data</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">Generate sample data for testing reports</p>
          </div>
          <Button
            onClick={seedDemoData}
            disabled={loading}
            variant="outline"
            size="sm"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
            data-testid="seed-demo-data-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
            Seed Demo Data
          </Button>
        </div>
      </div>

      {/* Analytics Preview Sections */}
      {claimsData && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="claims-preview">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-500" />
            Claims Analytics Preview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Claims</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{claimsData.total_claims}</p>
            </div>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Approved</p>
              <p className="text-2xl font-bold text-emerald-600">{claimsData.approved_count}</p>
              <p className="text-xs text-emerald-600">RM {claimsData.approved_amount?.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Pending</p>
              <p className="text-2xl font-bold text-amber-600">{claimsData.pending_count}</p>
              <p className="text-xs text-amber-600">RM {claimsData.pending_amount?.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{claimsData.rejected_count}</p>
            </div>
          </div>
          {claimsData.by_type && Object.keys(claimsData.by_type).length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">By Type</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(claimsData.by_type).map(([type, data]) => (
                  <div key={type} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 rounded-full text-sm text-purple-700 dark:text-purple-300">
                    {type.replace('_', ' ')}: {data.count} (RM {data.amount?.toLocaleString()})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {attendanceData && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="attendance-preview">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            Attendance Analytics Preview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Records</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{attendanceData.total_records}</p>
            </div>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Present</p>
              <p className="text-2xl font-bold text-emerald-600">{attendanceData.present}</p>
            </div>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Late</p>
              <p className="text-2xl font-bold text-amber-600">{attendanceData.late}</p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Absent</p>
              <p className="text-2xl font-bold text-red-600">{attendanceData.absent}</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Attendance Rate</p>
              <p className="text-2xl font-bold text-blue-600">{attendanceData.attendance_rate}%</p>
            </div>
          </div>
        </div>
      )}

      {leaveData && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="leave-preview">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-pink-500" />
            Leave Analytics Preview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Applications</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{leaveData.total_applications}</p>
            </div>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Approved</p>
              <p className="text-2xl font-bold text-emerald-600">{leaveData.approved_count}</p>
            </div>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Pending</p>
              <p className="text-2xl font-bold text-amber-600">{leaveData.pending_count}</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Days Taken</p>
              <p className="text-2xl font-bold text-blue-600">{leaveData.total_days_taken}</p>
            </div>
          </div>
          {leaveData.by_type && Object.keys(leaveData.by_type).length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">By Leave Type</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(leaveData.by_type).map(([type, data]) => (
                  <div key={type} className="px-3 py-1 bg-pink-100 dark:bg-pink-900/30 rounded-full text-sm text-pink-700 dark:text-pink-300">
                    {type}: {data.count} ({data.days} days)
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {performanceData && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="performance-preview">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-teal-500" />
            Performance Analytics Preview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Reviews</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{performanceData.total_reviews}</p>
            </div>
            <div className="p-4 bg-teal-50 dark:bg-teal-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Average Rating</p>
              <p className="text-2xl font-bold text-teal-600">{performanceData.average_rating}/5</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Rating Distribution</p>
              <div className="flex gap-1 mt-1">
                {[5, 4, 3, 2, 1].map(rating => (
                  <div key={rating} className="text-xs text-center">
                    <span className="text-slate-600 dark:text-slate-400">{rating}★</span>
                    <p className="font-bold text-slate-900 dark:text-slate-100">{performanceData.rating_distribution?.[rating] || 0}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {performanceData.by_department && Object.keys(performanceData.by_department).length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">By Department</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(performanceData.by_department).map(([dept, data]) => (
                  <div key={dept} className="px-3 py-1 bg-teal-100 dark:bg-teal-900/30 rounded-full text-sm text-teal-700 dark:text-teal-300">
                    {dept}: {data.count} reviews (Avg: {data.avg_rating || 'N/A'})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!claimsData && !attendanceData && !leaveData && !performanceData && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <BarChart3 className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">
            Click "Preview" on any report card to see analytics, or download directly
          </p>
        </div>
      )}
    </div>
  );
}
